import { landmineNames } from '@mm/constants/landmine-names';
import { useViewActions } from '@mm/state/actions/viewActions';
import chatbotSidebarActions from '@mm/state/modules/chatbotSidebarState/actions';
import {
  SET_TOUR,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { tourOpenSelector } from '@mm/state/selectors/modalSelector';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import Tour from 'reactour';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { RouterComponentProps } from '../../common/helpers/routes/interfaces';
import { UserRoleContainer } from '../../common/hooks/use-route-guard';
import { filteredSidebarMenuSelector } from '../filteredSidebarMenuSelector';
import './BotBuilderSidebar.scss';
import { SidebarMenuDefinition, SIDEBAR_MENU } from './sidebar-menu';
import SidebarHeader from './SidebarHeader/SidebarHeader';
import { SidebarMenuGroup } from './SidebarMenuGroup/SidebarMenuGroup';
import { steps } from './Tour/Steps';
import UserDropdown from './UserDropdown/UserDropdown';
import { FollowConvosItem } from './ActivePageSlideMenu/ActivePageSlideMenu';
import { NewSidebarHeader } from './SidebarHeader/NewSidebarHeader/NewSidebarHeader';
import { useFreeTrialUpgradeModal } from '../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

type OuterProps = {
  hideSubNav?: boolean;
};

export type GuardedProLinkClick = (
  e: MouseEvent<HTMLAnchorElement>,
  menuItem: SidebarMenuDefinition,
) => void;

type InnerProps = RouterComponentProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const getDefaultExpandedId = (pathname: string) => {
  const menuGroupUrl = `/${pathname.split('/')[3]}`;

  for (const menuGroup of SIDEBAR_MENU) {
    for (const menuItem of menuGroup.menuItems) {
      if (menuGroupUrl.includes(menuItem.url)) {
        return menuGroup.id;
      }

      const childRoutes = menuItem.childRoutes || [];

      for (const childRoute of childRoutes) {
        if (menuGroupUrl.includes(childRoute)) {
          return menuGroup.id;
        }
      }
    }

    if (menuGroup.url && menuGroupUrl.includes(menuGroup.url)) {
      return menuGroup.id;
    }
  }
};

const BotBuilderSidebar = (props: Props) => {
  const {
    activeFacebookPage,
    filteredSidebarMenu,
    setSidebarMinimized,
    sidebarMinimized,
    toggleMenuGroupExpanded,
    expandedMenuGroups,
    activeChatbotId,
    resetMenu,
    activeFacebookPagePlanInfo,
    company,
    activeChatbot,
    hideSubNav,
  } = props;
  const { location } = useRouter();
  const dispatch = useDispatch();
  const twilioNumber = useSelector(hasSMSNumberSelector);
  const tourOpenState = useSelector(tourOpenSelector);
  const { openUniversalUpgradeLandmineModal: showUpgradeModal } =
    useModalActions();
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);
  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();

  const [followUpConvos, setFollowUpConvos] = useState(
    {} as Record<number, FollowConvosItem | undefined>,
  );

  const onRequestCloseTour = () => {
    dispatch({
      type: SET_TOUR,
      payload: false,
    });
  };

  const {
    roleFlags: { isLiveChatAgent, isMonitorLeadsRole },
  } = UserRoleContainer.useContainer();

  const menuItems = isLiveChatAgent
    ? filteredSidebarMenu.filter(({ id }) => id === 'INBOX')
    : isMonitorLeadsRole
    ? filteredSidebarMenu.filter(({ id }) => id === 'LEADS-CONTACTS')
    : filteredSidebarMenu;

  useEffect(() => {
    const retrievedObject = localStorage.getItem('followupNumber');
    if (retrievedObject) {
      setFollowUpConvos(JSON.parse(retrievedObject));
    }
  }, []);

  useEffect(() => {
    const defaultExpandedId = getDefaultExpandedId(location.pathname);

    if (defaultExpandedId) {
      toggleMenuGroupExpanded(defaultExpandedId);
    }

    return () => {
      resetMenu();
    };
  }, [location.pathname]);

  const guardedProLinkClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, menuItem: SidebarMenuDefinition) => {
      const { featureFlag } = menuItem;

      if (featureFlag && !featureFlags[featureFlag]) {
        e.preventDefault();
        showUpgradeModal({
          landmineName: `${
            landmineNames['BOT-BUILDER-SIDEBAR__PRO-LINK-CLICK']
          }__${kebabCase(menuItem.name).toUpperCase()}`,
          feature: featureFlag,
        });
      }
    },
    [activeFacebookPagePlanInfo, showUpgradeModal],
  );

  const classList = classnames('bot-builder-sidebar', {
    'bot-builder-sidebar__with-automations-and-free-tial':
      isFreeTrialPromotionActive && isAutomationsEnabled,
  });

  const viewActions = useViewActions();
  const openSlideoutPageMenu = () => {
    viewActions.showSlideoutMenu('pages', {});
  };

  const topLinkMenu = (
    <div className="bot-builder-sidebar__top-link-area">
      {menuItems.map(menuGroup => (
        <SidebarMenuGroup
          facebookPage={activeFacebookPage}
          key={menuGroup.id}
          menuGroup={menuGroup}
          sidebarMinimized={sidebarMinimized}
          toggleMenuGroupExpanded={toggleMenuGroupExpanded}
          expandedMenuGroups={expandedMenuGroups}
          activeChatbotId={activeChatbotId}
          guardedProLinkClick={guardedProLinkClick}
          disableMenuItems={menuGroup.id === 'SMS' && !twilioNumber}
          hidden={hideSubNav}
          followUpConvos={followUpConvos}
          isFreeTrialActive={isFreeTrialPromotionActive}
          isAutomationsEnabled={isAutomationsEnabled}
          isBeta={menuGroup.isBeta}
        />
      ))}
    </div>
  );

  return (
    <>
      <Tour
        rounded={5}
        steps={steps}
        isOpen={tourOpenState}
        onRequestClose={onRequestCloseTour}
        maskSpace={15}
        showButtons={false}
        showNavigation={false}
        showCloseButton={false}
        showNumber={false}
        className="tour"
      />

      <div className={classList}>
        {activeFacebookPage && company && !isAutomationsEnabled && (
          <SidebarHeader
            facebookPage={activeFacebookPage}
            onMenuClick={openSlideoutPageMenu}
            setSidebarMinimized={setSidebarMinimized}
            sidebarMinimized={sidebarMinimized}
            company={company}
            activeChatbot={activeChatbot || null}
          >
            {topLinkMenu}
          </SidebarHeader>
        )}

        {activeFacebookPage && company && isAutomationsEnabled && (
          <NewSidebarHeader
            facebookPage={activeFacebookPage}
            onMenuClick={openSlideoutPageMenu}
            setSidebarMinimized={setSidebarMinimized}
            sidebarMinimized={sidebarMinimized}
            company={company}
            activeChatbot={activeChatbot || null}
            isFreeTrialActive={isFreeTrialPromotionActive}
          >
            {topLinkMenu}
          </NewSidebarHeader>
        )}

        {!isAutomationsEnabled && (
          <div className="bot-builder-sidebar__menu-groups-wrapper">
            <div className="bot-builder-sidebar__bottom-link-area">
              <UserDropdown />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  setSidebarMinimized: (isMinimized: boolean) =>
    dispatch(chatbotSidebarActions.setIsMinimized(isMinimized)),
  toggleMenuGroupExpanded: (menuGroupId: string) =>
    dispatch(chatbotSidebarActions.toggleMenuGroupExpanded(menuGroupId)),
  resetMenu: () => dispatch(chatbotSidebarActions.resetMenu()),
});

const mapStateToProps = (state: StoreState) => ({
  filteredSidebarMenu: filteredSidebarMenuSelector(state),
  sidebarMinimized: state.chatbotSidebarState.isMinimized,
  expandedMenuGroups: state.chatbotSidebarState.expandedMenuGroups,
  activeFacebookPagePlanInfo: activeFacebookPagePlanInfoSelector(state).info,
  activeFacebookPage: activeFacebookPageSelector(state),
  activeChatbotId: activeChatbotIdSelector(state),
  company: activeCompanySelector(state),
  activeChatbot: state.chatbot.activeChatbot,
  fbAppId: state.env.fbAppId,
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BotBuilderSidebar);
